import React, { useState, useEffect } from 'react';
import { ServiceNowAPI } from '../services/api';
import './ServiceNowTickets.css';

function ServiceNowTickets() {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    const fetchTickets = async () => {
      if (!isMounted) return;
      
      try {
        setLoading(true);
        const ticketsData = await ServiceNowAPI.getTickets();
        if (isMounted) {
          setTickets(ticketsData);
          setError(null);
          setRetryCount(0); // Reset retry count on success
        }
      } catch (err) {
        console.error('Error fetching tickets:', err);
        
        // If we haven't exceeded max retries and the component is still mounted
        if (retryCount < maxRetries && isMounted) {
          console.log(`Retrying fetch, attempt ${retryCount + 1}`);
          timeoutId = setTimeout(() => {
            if (isMounted) {
              setRetryCount(prev => prev + 1);
            }
          }, retryDelay * (retryCount + 1)); // Exponential backoff
          return;
        }
        
        if (isMounted) {
          setError('Failed to fetch tickets. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Only fetch if we're retrying or don't have any tickets yet
    if (retryCount > 0 || !tickets.length) {
      fetchTickets();
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [retryCount, tickets.length]); // Include tickets.length in dependencies

  const filteredTickets = React.useMemo(() => 
    tickets?.filter(ticket =>
      ticket.number?.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.priority?.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.location?.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.status?.toLowerCase().includes(searchText.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchText.toLowerCase())
    ) || []
  , [tickets, searchText]);

  if (loading && !tickets.length) {
    return <div className="loading">Loading tickets...</div>;
  }

  if (error && !tickets.length) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button 
          className="retry-button"
          onClick={() => setRetryCount(prev => prev + 1)}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="servicenow-container">
      <div className="ticket-list">
        <h2>Tickets</h2>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="search-input"
          />
          <button 
            className="search-button"
            onClick={() => setSearchText('')}
          >
            Clear
          </button>
        </div>
        <div className="tickets">
          {filteredTickets.map(ticket => (
            <div
              key={ticket.id}
              className={`ticket-item ${selectedTicket?.id === ticket.id ? 'selected' : ''}`}
              onClick={() => setSelectedTicket(ticket)}
            >
              <div className="ticket-header">
                <span className="ticket-number">{ticket.number}</span>
                <span className={`ticket-priority ${ticket.priority.toLowerCase()}`}>
                  {ticket.priority}
                </span>
                <span className={`ticket-status ${ticket.status.toLowerCase()}`}>
                  {ticket.status}
                </span>
              </div>
              <div className="ticket-title">{ticket.title}</div>
              <div className="ticket-subtitle">
                <span>{new Date(ticket.createDate).toLocaleDateString()}</span>
                <span>{ticket.location}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="ticket-details">
        {selectedTicket ? (
          <>
            <h2>Ticket Details</h2>
            <div className="detail-content">
              <h3>{selectedTicket.number}</h3>
              <p><strong>ID:</strong> {selectedTicket.id}</p>
              <p><strong>Title:</strong> {selectedTicket.title}</p>
              <p><strong>Created:</strong> {new Date(selectedTicket.createDate).toLocaleString()}</p>
              <p><strong>Priority:</strong> {selectedTicket.priority}</p>
              <p><strong>Status:</strong> {selectedTicket.status}</p>
              <p><strong>Location:</strong> {selectedTicket.location}</p>
              <p><strong>Description:</strong></p>
              <p className="description-text">{selectedTicket.description}</p>
            </div>
          </>
        ) : (
          <div className="no-selection">
            <p>Select a ticket to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ServiceNowTickets;
